import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiTrendingUp, FiMoon, FiSmile } = FiIcons;

const BenefitsSection = () => {
  const benefits = [
    {
      icon: FiClock,
      title: 'Save 10+ Hours Per Week',
      metric: '40+ Hours/Month',
      description: 'Automate 90% of the lead qualification process and eliminate time-wasting calls for good. What would you do with an extra 40 hours a month?'
    },
    {
      icon: FiTrendingUp,
      title: 'Increase Client Conversion by 35%',
      metric: '35%+ Higher Close Rate',
      description: 'Only speak to pre-qualified, high-intent prospects who are ready to buy. Less selling, more enrolling. Your sales calls become simple strategy sessions.'
    },
    {
      icon: FiMoon,
      title: 'Fill Your Pipeline 24/7',
      metric: '24/7 Lead Capture',
      description: 'Your lead generation doesn\'t stop when you do. Capture and qualify leads from different time zones around the clock, turning your website into an automated client-booking machine.'
    },
    {
      icon: FiSmile,
      title: 'Improve Client Experience',
      metric: 'Instant Answers',
      description: 'Provide instant, intelligent answers to prospects\' questions, building trust from the very first interaction. No more "I\'ll get back to you tomorrow."'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Stop Being a <span className="text-orange-600">Salesperson</span>
          </h2>
          <p className="text-3xl text-gray-700">
            Start Being the <span className="text-green-600 font-bold">CEO of Your Business</span>
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-xl border-2 border-gray-100 hover:border-blue-200 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-start gap-6">
                <div className="bg-gradient-to-br from-blue-100 to-green-100 w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={benefit.icon} className="text-3xl text-blue-600" />
                </div>
                
                <div className="flex-1">
                  <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold mb-4 inline-block">
                    {benefit.metric}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {benefit.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ROI Callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-4xl mx-auto mt-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-10 rounded-3xl shadow-2xl text-center"
        >
          <h3 className="text-3xl font-bold mb-4">The Bottom Line</h3>
          <p className="text-xl text-blue-100 mb-6">
            Handle 10x more lead flow without adding headcount. The AI acts as your first line of sales development, 
            allowing you to scale your marketing efforts confidently.
          </p>
          <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm px-8 py-4 rounded-xl">
            <span className="text-4xl">🚀</span>
            <span className="text-2xl font-bold">Scale Without Hiring</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;